import { PlanType, ServiceType } from '~/types/subscriptionTypes';
import { CreditPackageType, PackageTierType } from '~/types/packagesType';
import { screens } from '~/theme';
import { getSubscriptionServices } from '~/utils';

export type PackagesGridPlanType = {
  id: number;
  key: string;
  checkoutId: number;
  type: 'subscription' | 'package';
  is_top_up: boolean;
  is_rental: boolean;
  title: string | null;
  description: string | null;
  flag: number;
  label: string | null;
  price: number | string;
  activation_fee?: string | number | null;
  price_before_discount: number | null;
  discount_percentage: number | null;
  durationMonths: number | null;
  credit: string | null;
  tiers: PackageTierType[] | null;
  services: ServiceType[];
};

export function getPriceWithTaxes(
  price: number,
  taxesPercentage?: number | null,
) {
  return price * (1 + Number(taxesPercentage || 0) / 100);
}

export function getPackagesFirstRowItemsCount(width: number) {
  const xlScreenSize = Number(screens.xl.replace('px', ''));
  const xxlScreenSize = Number(screens['2xl'].replace('px', ''));

  if (width >= xxlScreenSize) return 4;
  if (width >= xlScreenSize) return 3;
  return 2;
}

export function getPackagesGridClass(cardsCount: number, width: number) {
  return `packages-card-grid ${
    cardsCount < getPackagesFirstRowItemsCount(width)
      ? 'justify-center'
      : 'justify-start'
  }`;
}

export function getPackageTierLocations(tiers: PackageTierType[]) {
  return tiers ? tiers.flatMap((tier) => tier.locations ?? []) : [];
}

export function buildPackagesPlans({
  subscriptionPlans,
  packages,
}: {
  subscriptionPlans?: PlanType[];
  packages?: CreditPackageType[];
}) {
  const normalizedSubscriptions: PackagesGridPlanType[] =
    subscriptionPlans?.map((plan) => {
      const hasDiscount = Boolean(plan.price_before_discount);
      const priceBeforeDiscount = plan.price_before_discount ?? 0;
      const amountOfDiscount = priceBeforeDiscount - Number(plan.price);
      const discountPercentage = hasDiscount
        ? Math.round((amountOfDiscount / priceBeforeDiscount) * 100)
        : 0;

      return {
        id: plan.id,
        key: `subscription-${plan.id}`,
        checkoutId: plan.id,
        type: 'subscription',
        is_top_up: false,
        is_rental: false,
        title: plan.title,
        description: plan.description,
        flag: plan.flag,
        label: null,
        price: plan.price,
        activation_fee: plan.activation_fees ?? null,
        price_before_discount: plan.price_before_discount,
        discount_percentage: discountPercentage,
        durationMonths: plan.duration_days
          ? Number(plan.duration_days) / 30
          : null,
        credit: plan.subscription_plan_package[0]?.package?.credit ?? null,
        tiers: plan.subscription_plan_package.flatMap(
          (subscriptionPlanPackage) =>
            subscriptionPlanPackage.package.tiers ?? [],
        ),
        services: getSubscriptionServices(plan),
      };
    }) ?? [];

  const normalizedPackages: PackagesGridPlanType[] =
    packages?.map((currentPackage) => ({
      id: currentPackage.id,
      key: `package-${currentPackage.id}`,
      checkoutId: currentPackage.id,
      type: 'package',
      is_top_up: currentPackage.is_top_up,
      is_rental: currentPackage.is_rental,
      title: currentPackage.title,
      description: currentPackage.description,
      flag: currentPackage.flag,
      label: currentPackage.label ?? currentPackage.package_type_label,
      price: getPriceWithTaxes(
        Number(currentPackage.price_after_discount),
        currentPackage.taxes_percentage,
      ),
      activation_fee: currentPackage.activation_fee ?? null,
      price_before_discount: getPriceWithTaxes(
        currentPackage.price,
        currentPackage.taxes_percentage,
      ),
      discount_percentage: currentPackage.discount_percentage ?? null,
      durationMonths: null,
      credit: currentPackage.credit,
      tiers: currentPackage.tiers ?? [],
      services: currentPackage.package_services,
    })) ?? [];

  return [...normalizedSubscriptions, ...normalizedPackages];
}
